import { useEffect, useState, useRef } from 'react'
import { DocumentArrowDownIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import ButtonComponent from 'components/common/ButtonComponent'
import TableComponent from 'components/common/TableComponent'
import PickingDataEditModal from 'components/modals/PickingDataEditModal'
import { parseDate } from 'utils'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import { useAuth } from 'auth/AuthContext'
import { DateTime } from 'luxon'

export default function PickingDataEdit () {
  const { user } = useAuth()
  const hasFetched = useRef(false)
  const currentFilterDate = useRef(null)
  const [selectedRows, setSelectedRows] = useState([])
  const [state, setState] = useState({
    pickingData: [],
    currentDate: parseDate(new Date()),
    filterDate: DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd'),
    searchTerm: '',
    showEditModal: false,
    selectedPickingData: null,
    isBulkEdit: false
  })

  const getPickingData = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/data-edit/picking-data/${user.farmId}?filterDate=${new Date(state.filterDate).toISOString()}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      setState(s => ({ ...s, pickingData: response.data }))
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const openSingleEditModal = (row) => {
    setState(s => ({ ...s, showEditModal: true, selectedPickingData: row, isBulkEdit: false }))
  }

  const openBulkEditModal = () => {
    setState(s => ({ ...s, showEditModal: true, selectedPickingData: null, isBulkEdit: true }))
  }

  const closeModal = () => {
    setState(s => ({ ...s, showEditModal: false, selectedPickingData: null, isBulkEdit: false }))
  }

  const handleSave = async (formData) => {
    try {
      if (state.isBulkEdit) {
        const ids = selectedRows.map(r => r.pickingDataId)
        await makeRequest({
          method: 'PATCH',
          url: '/management/data-edit/picking-data/bulk',
          data: { ids, ...formData },
          headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
        })
      } else {
        await makeRequest({
          method: 'PATCH',
          url: `/management/data-edit/picking-data/${state.selectedPickingData.pickingDataId}`,
          data: formData,
          headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
        })
      }
      showAlert({ text: 'Picking data updated', icon: 'success' })
      closeModal()
      await getPickingData()
    } catch (error) {
      showAlert({ text: 'Failed to update picking data', icon: 'error' })
      console.error('Error updating picking data:', error)
    }
  }

  const handleDelete = async (row) => {
    try {
      await makeRequest({
        method: 'DELETE',
        url: `/management/data-edit/picking-data/${row.pickingDataId}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      showAlert({ text: 'Picking data deleted', icon: 'success' })
      await getPickingData()
    } catch (error) {
      showAlert({ text: 'Failed to delete picking data', icon: 'error' })
      console.error('Error deleting picking data:', error)
    }
  }

  const filteredData = state.pickingData.filter(item => {
    if (!state.searchTerm) return true
    return (
      (item.block ?? '').toLowerCase().includes(state.searchTerm) ||
      (item.workerName ?? '').toLowerCase().includes(state.searchTerm) ||
      (item.containerName ?? '').toLowerCase().includes(state.searchTerm)
    )
  })

  useEffect(() => {
    if (hasFetched.current && currentFilterDate.current === state.filterDate) return
    hasFetched.current = true
    currentFilterDate.current = state.filterDate
    getPickingData()
  }, [state.filterDate])

  return (
    <div className='p-6 bg-white shadow-md border border-gray-300 border-t-0'>
      <div className='section-header'>
        <div className='header-left'>
          <div className='date-filter'>
            <span>Today ({state.currentDate})</span>
          </div>
        </div>

        <div className='flex items-center gap-4 mb-6'>
          {selectedRows.length > 0 && (
            <ButtonComponent className='flex items-center gap-2' onClick={openBulkEditModal}>
              <PencilSquareIcon className='w-5 h-5' />
              <span>Edit selection ({selectedRows.length})</span>
            </ButtonComponent>
          )}
          <ButtonComponent className='flex items-center gap-2'>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>Export as Excel</span>
          </ButtonComponent>
          <ButtonComponent className='flex items-center gap-2'>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>Export as PDF</span>
          </ButtonComponent>
        </div>
      </div>

      <div className='search-container flex gap-4 mb-4'>
        <input
          type='text'
          placeholder='Search'
          value={state.searchTerm}
          onChange={(e) => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className='w-48 mt-1 p-2 border rounded-md shadow-sm'
          data-align='left'
        />
        <input
          type='date'
          value={state.filterDate}
          onChange={e => setState(s => ({ ...s, filterDate: e.target.value }))}
          className='w-40 mt-2 mb-2 p-2 border rounded-md shadow-sm'
          data-align='left'
        />
      </div>

      <div className='data-table'>
        {state.pickingData.length !== 0 && (
          <TableComponent
            tableData={{
              hasCheckbox: true,
              columns: [
                { key: 'block', label: 'Block' },
                { key: 'workerName', label: 'Worker' },
                { key: 'date', label: 'Date', cell: (value) => parseDate(value) },
                { key: 'containerName', label: 'Container' },
                { key: 'totalUnits', label: 'Total Units' },
                { key: 'credit', label: 'Credit' },
                { key: 'actions', label: 'Manage' }
              ],
              rows: filteredData
            }}
            handleEdit={openSingleEditModal}
            handleDelete={handleDelete}
            onSelectionChange={setSelectedRows}
            defaultSortColumn='date'
          />
        )}
      </div>

      <PickingDataEditModal
        isOpen={state.showEditModal}
        handleClose={closeModal}
        handleSave={handleSave}
        selectedPickingData={state.selectedPickingData}
        isBulk={state.isBulkEdit}
        bulkCount={selectedRows.length}
        farmId={user.farmId}
      />
    </div>
  )
}
